import type { CaseState, JiraState } from 'src/components/SupportCase/types';

/**
 * One vocabulary for the support case's states (ENG-4220).
 *
 * There used to be three copies of this: `CaseEvent.tsx` said "In progress" and
 * "Done", `SupportCasesPage.tsx` said "Desk in progress" and "Desk done", and
 * `CaseStream.tsx` carried its own case-state map. Same three states, two
 * vocabularies, and a user reading a list row and the case header saw different words
 * for the same thing. This module is the one copy.
 *
 * The wording is deliberate: **Pronto Housing Support** is the team the user is
 * talking to. "Desk" was internal shorthand that read as a place, not people.
 *
 * Colours come from the status registry's semantics and are unchanged by the rename:
 * pending is neutral, in progress is informational, done is success.
 */

export type StateColor = 'default' | 'info' | 'warning' | 'success';

export interface StateMeta {
  label: string;
  /**
   * The same state in the words the floating panel has room for (ENG-4299).
   *
   * The panel header is 420 px wide and one 29-character sentence was taking 208 of
   * its 388 usable pixels, which is what pushed the context line into a 67 px column
   * four lines tall. The panel renders this form and puts `label` in the chip's
   * tooltip, so no meaning is lost — but it is a *second* label for one state, which
   * the status registry does not define. It lives here, beside the long form, so a
   * rename is one edit and a reader cannot find two vocabularies again.
   */
  shortLabel: string;
  color: StateColor;
}

/** The case's own lifecycle. */
export const CASE_STATE_META: Record<CaseState, StateMeta> = {
  agent_active: {
    label: 'Pronto Assistant is working',
    shortLabel: 'Assistant working',
    color: 'info',
  },
  awaiting_user: {
    label: 'Waiting on you',
    shortLabel: 'Waiting on you',
    color: 'warning',
  },
  awaiting_approval: {
    label: 'Waiting on your approval',
    shortLabel: 'Waiting on approval',
    color: 'warning',
  },
  filed: {
    label: 'Sent to Pronto Housing Support',
    shortLabel: 'Sent to Support',
    color: 'info',
  },
  resolved: { label: 'Resolved', shortLabel: 'Resolved', color: 'success' },
};

/** The three words a filed request may be described with — and no others. */
export const JIRA_STATE_META: Record<Exclude<JiraState, ''>, StateMeta> = {
  pending: {
    label: 'Sent to Pronto Housing Support',
    shortLabel: 'Sent to Support',
    color: 'default',
  },
  'in progress': {
    label: 'Pronto Housing Support is working on it',
    shortLabel: 'Support is working',
    color: 'info',
  },
  done: {
    label: 'Pronto Housing Support marked this done',
    shortLabel: 'Support marked it done',
    color: 'success',
  },
};

/** A case state's meta, defaulting to the one the panel opens in. */
export const caseStateMeta = (state: CaseState | undefined): StateMeta =>
  CASE_STATE_META[state ?? 'agent_active'] ?? CASE_STATE_META.agent_active;

/** A Jira state's meta, or `null` when the case is not filed or not observed yet. */
export const jiraStateMeta = (
  state: JiraState | undefined
): StateMeta | null => {
  if (!state) {
    return null;
  }
  return JIRA_STATE_META[state] ?? null;
};

/**
 * The chips a row shows — at most one (ENG-4251, PA-08).
 *
 * A filed case carried the case chip and the Jira chip with the *same* label, so one
 * card showed "Sent to Pronto Housing Support" twice. When the case is filed the
 * ticket state is the useful one; the case chip is the fallback only while the ticket
 * state is unknown.
 */
export const primaryChips = (
  state: CaseState | undefined,
  jiraState: JiraState | undefined
): StateMeta[] => {
  const jira = jiraStateMeta(jiraState);
  if (state === 'filed' && jira) {
    return [jira];
  }
  return [caseStateMeta(state)];
};
